// WistiaJsonInput.tsx
import React, { ComponentType, useEffect, useMemo } from "react"
import { PatchEvent, set } from "sanity"

type WistiaJson = {
  url?: string
  thumbnailUrl?: string
}

/** Fetch a Wistia oEmbed thumbnail URL */
async function getWistiaThumbnailUrl(wistiaUrl: string, width?: number) {
  const apiUrl = `https://fast.wistia.net/oembed?url=${encodeURIComponent(
    wistiaUrl
  )}`
  const res = await fetch(apiUrl)
  if (!res.ok) {
    throw new Error(
      `Wistia oEmbed request failed: ${res.status} ${res.statusText}`
    )
  }
  const json = (await res.json()) as { thumbnail_url?: string }
  if (!json.thumbnail_url)
    throw new Error("No thumbnail_url in Wistia oEmbed response")
  const thumbUrl = json.thumbnail_url
  if (width) {
    const sep = thumbUrl.includes("?") ? "&" : "?"
    return `${thumbUrl}${sep}image_resize=${width}`
  }
  return thumbUrl
}

/**
 * Custom input for a `json` field that stores:
 * {
 *   url: string
 *   thumbnailUrl?: string
 * }
 *
 * We render simple inputs for url, compute thumbnailUrl automatically,
 * and write the WHOLE json value with a single `set(...)`. No sibling patches.
 */
const WistiaJsonInput: ComponentType<any> = props => {
  const { value, onChange, renderDefault } = props as {
    value?: WistiaJson
    onChange: (evt: PatchEvent) => void
    renderDefault: (p: any) => React.ReactNode
  }

  // Always treat value as a plain object
  const v: WistiaJson = useMemo(
    () => (value && typeof value === "object" ? value : {}),
    [value]
  )

  const setValue = (next: WistiaJson) => onChange(PatchEvent.from([set(next)]))

  // Resolve thumbnail whenever URL changes (and keep everything inside this json field)
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const url = (v.url || "").trim()
      if (!url) {
        // Clear thumbnail if URL is empty
        if (v.thumbnailUrl) setValue({ ...v, thumbnailUrl: undefined })
        return
      }
      // Optimistic "loading" marker is optional—skip to keep the value clean
      try {
        const thumbnailUrl = await getWistiaThumbnailUrl(url)
        if (cancelled) return
        // Only write if changed to avoid patch spam
        if (thumbnailUrl !== v.thumbnailUrl) {
          setValue({ ...v, thumbnailUrl })
        }
      } catch (err) {
        console.error("Failed to fetch Wistia thumbnail:", err)
        if (cancelled) return
        // You can store an error token or simply clear it:
        if (v.thumbnailUrl) setValue({ ...v, thumbnailUrl: undefined })
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.url])

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ marginTop: 8, opacity: 0.9 }}>{renderDefault?.(props)}</div>

      {v.thumbnailUrl ? (
        <div style={{ display: "grid", gap: 4 }}>
          <a href={v.thumbnailUrl} target="_blank" rel="noreferrer">
            <img
              src={v.thumbnailUrl}
              alt="Wistia thumbnail"
              style={{
                maxWidth: "50%",
                height: "auto",
                border: "1px solid #eee",
              }}
            />
          </a>
        </div>
      ) : null}
    </div>
  )
}

export default WistiaJsonInput
